import {
    EmbeddingProviderId,
    ImageEmbeddingSearchRequest,
    ImageEmbeddingSearchRequestBase,
    LookitItemCategoryId,
    UploadImageDeleteApiRequest,
    UploadImageDeleteArtifactResult,
    VectorArtifact,
    VectorArtifactStorageStatus
} from './domain';

export type ImageEmbeddingProviderKind = 'mock' | 'internal_api' | 'external_ai';

export type ImageEmbeddingProviderMetadata = Pick<
    ImageEmbeddingSearchRequestBase,
    | 'sourceRequestId'
    | 'uploadImageId'
    | 'searchTargetId'
    | 'selectedRegion'
    | 'selectedCrop'
    | 'selectedCategoryId'
    | 'subcategoryHint'
    | 'brandHints'
    | 'ocrHints'
    | 'regionSource'
    | 'userConfirmed'
    | 'selectedCandidateId'
    | 'privacyRedactionIds'
    | 'externalAiPrivacyGate'
> & {
    cropUrl?: string | null;
    expiresAt?: string | null;
    retentionSeconds?: number;
    now?: Date;
};

export interface ImageEmbeddingProviderInfo {
    id: EmbeddingProviderId;
    label: string;
    kind: ImageEmbeddingProviderKind;
    modelVersion: string;
    dimensions: number;
    rawVectorExposed: false;
    deletesDerivedData: boolean;
}

export interface ImageEmbeddingCreateResponse {
    embeddingId: string;
    vectorId: string;
    modelVersion: string;
    dimensions: number;
    createdAt: string;
    expiresAt: string | null;
}

export interface DeleteImageEmbeddingInput {
    embeddingId?: string | null;
    vectorId?: string | null;
    cropImageId?: string | null;
    uploadImageId?: string | null;
    requestedAt?: string | null;
    reason?: UploadImageDeleteApiRequest['reason'];
}

export interface DeleteImageEmbeddingResult {
    ok: true;
    provider: EmbeddingProviderId;
    status: 'deleted' | 'already_deleted' | 'no_records';
    deletedAt: string;
}

export interface EmbeddingProvider {
    createImageEmbedding(
        cropImageId: string,
        metadata: ImageEmbeddingProviderMetadata
    ): Promise<ImageEmbeddingCreateResponse>;
    getProviderInfo(): ImageEmbeddingProviderInfo;
    deleteEmbedding(input: DeleteImageEmbeddingInput): Promise<DeleteImageEmbeddingResult>;
}

export interface CreateVectorArtifactInput {
    uploadImageId?: string | null;
    cropImageId: string;
    selectedCategoryId: LookitItemCategoryId | string;
    provider: EmbeddingProviderId;
    modelVersion: string;
    embeddingId: string;
    vectorId: string;
    dimensions: number;
    createdAt: string;
    expiresAt?: string | null;
    storageStatus?: VectorArtifactStorageStatus;
}

export interface VectorArtifactRepository {
    dataKind: 'embedding';
    repository: 'embedding_vector_repository';
    createVectorArtifact(input: CreateVectorArtifactInput): Promise<VectorArtifact>;
    getVectorArtifact(id: string): Promise<VectorArtifact | null>;
    listByUploadImageId(uploadImageId: string): Promise<VectorArtifact[]>;
    deleteByUploadImageId(input: {
        uploadImageId: string;
        requestedAt: string;
        reason: UploadImageDeleteApiRequest['reason'];
    }): Promise<UploadImageDeleteArtifactResult>;
}

export interface CreateImageEmbeddingVectorArtifactOptions {
    expiresAt?: string | null;
    retentionSeconds?: number;
    now?: Date;
}

const defaultMockModelVersion = 'lookit-mock-image-embedding-v1';
const defaultMockDimensions = 8;
const defaultEmbeddingRetentionSeconds = 7 * 24 * 60 * 60;

interface StoredMockEmbedding {
    cropImageId: string;
    uploadImageId: string | null;
    response: ImageEmbeddingCreateResponse;
    deletedAt: string | null;
}

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function addSeconds(date: Date, seconds: number): Date {
    return new Date(date.getTime() + seconds * 1000);
}

function nonEmptyText(value?: string | null): string {
    return typeof value === 'string' ? value.trim() : '';
}

function assertCropImageId(cropImageId?: string | null): string {
    const normalized = nonEmptyText(cropImageId);
    if (!normalized) throw new Error('cropImageId is required for image embedding artifact creation');
    return normalized;
}

function stableHash(input: string): string {
    let hash = 2166136261;
    for (let index = 0; index < input.length; index += 1) {
        hash ^= input.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}

function normalizedHintIdentity(hints: ImageEmbeddingProviderMetadata['brandHints']): string[] {
    return (hints || []).map((hint) => [
        hint.source,
        hint.normalizedText || hint.text,
        hint.confidence ?? null
    ].join(':'));
}

function embeddingIdentity(input: {
    cropImageId: string;
    metadata: ImageEmbeddingProviderMetadata;
    modelVersion: string;
    dimensions: number;
}): string {
    const metadata = input.metadata;
    return JSON.stringify({
        cropImageId: input.cropImageId,
        uploadImageId: metadata.uploadImageId || null,
        selectedCategoryId: metadata.selectedCategoryId,
        subcategoryHint: metadata.subcategoryHint || null,
        brandHints: normalizedHintIdentity(metadata.brandHints),
        ocrHints: normalizedHintIdentity(metadata.ocrHints),
        regionSource: metadata.regionSource,
        userConfirmed: metadata.userConfirmed,
        modelVersion: input.modelVersion,
        dimensions: input.dimensions
    });
}

function artifactResult(input: {
    status: UploadImageDeleteArtifactResult['status'];
    operation: UploadImageDeleteArtifactResult['operation'];
    deletedAt?: string | null;
    message?: string | null;
}): UploadImageDeleteArtifactResult {
    return {
        data_kind: 'embedding',
        status: input.status,
        repository: 'embedding_vector_repository',
        operation: input.operation,
        deleted_at: input.deletedAt ?? null,
        message: input.message ?? null
    };
}

export class MockEmbeddingProvider implements EmbeddingProvider {
    private readonly records = new Map<string, StoredMockEmbedding>();

    constructor(
        private readonly modelVersion = defaultMockModelVersion,
        private readonly dimensions = defaultMockDimensions
    ) { }

    getProviderInfo(): ImageEmbeddingProviderInfo {
        return {
            id: 'mock',
            label: 'LOOKIT deterministic mock image embedding provider',
            kind: 'mock',
            modelVersion: this.modelVersion,
            dimensions: this.dimensions,
            rawVectorExposed: false,
            deletesDerivedData: true
        };
    }

    async createImageEmbedding(
        cropImageIdInput: string,
        metadata: ImageEmbeddingProviderMetadata
    ): Promise<ImageEmbeddingCreateResponse> {
        const cropImageId = assertCropImageId(cropImageIdInput);
        if (!metadata.selectedCategoryId) throw new Error('selectedCategoryId is required for image embedding artifact creation');
        if (metadata.regionSource === 'full_image') {
            throw new Error('full image embedding fallback is not allowed for product search');
        }

        const now = metadata.now || new Date();
        const identity = embeddingIdentity({
            cropImageId,
            metadata,
            modelVersion: this.modelVersion,
            dimensions: this.dimensions
        });
        const identityHash = stableHash(identity);
        const response: ImageEmbeddingCreateResponse = {
            embeddingId: `embedding-mock-${identityHash}`,
            vectorId: `vector-mock-${identityHash}`,
            modelVersion: this.modelVersion,
            dimensions: this.dimensions,
            createdAt: now.toISOString(),
            expiresAt: metadata.expiresAt ?? addSeconds(now, metadata.retentionSeconds ?? defaultEmbeddingRetentionSeconds).toISOString()
        };

        this.records.set(response.embeddingId, {
            cropImageId,
            uploadImageId: metadata.uploadImageId || null,
            response: cloneData(response),
            deletedAt: null
        });

        return cloneData(response);
    }

    async deleteEmbedding(input: DeleteImageEmbeddingInput): Promise<DeleteImageEmbeddingResult> {
        const requestedAt = input.requestedAt || new Date().toISOString();
        const target = Array.from(this.records.values()).find((record) => (
            (input.embeddingId && record.response.embeddingId === input.embeddingId) ||
            (input.vectorId && record.response.vectorId === input.vectorId) ||
            (input.cropImageId && record.cropImageId === input.cropImageId)
        )) || null;

        if (!target) {
            return {
                ok: true,
                provider: 'mock',
                status: input.vectorId || input.embeddingId || input.cropImageId ? 'deleted' : 'no_records',
                deletedAt: requestedAt
            };
        }

        if (target.deletedAt) {
            return {
                ok: true,
                provider: 'mock',
                status: 'already_deleted',
                deletedAt: target.deletedAt
            };
        }

        target.deletedAt = requestedAt;
        return {
            ok: true,
            provider: 'mock',
            status: 'deleted',
            deletedAt: requestedAt
        };
    }
}

export class InMemoryVectorArtifactRepository implements VectorArtifactRepository {
    public readonly dataKind = 'embedding' as const;
    public readonly repository = 'embedding_vector_repository' as const;
    private readonly records = new Map<string, VectorArtifact>();

    constructor(private readonly provider?: EmbeddingProvider) { }

    async createVectorArtifact(input: CreateVectorArtifactInput): Promise<VectorArtifact> {
        const cropImageId = assertCropImageId(input.cropImageId);
        if (!input.selectedCategoryId) throw new Error('selectedCategoryId is required for vector artifact');
        if (!input.vectorId.trim()) throw new Error('vectorId is required for vector artifact');
        if (!input.embeddingId.trim()) throw new Error('embeddingId is required for vector artifact');

        const id = `vector-artifact-${stableHash(`${input.embeddingId}:${input.vectorId}`)}`;
        const artifact: VectorArtifact = {
            id,
            embeddingId: input.embeddingId,
            uploadImageId: input.uploadImageId || null,
            cropImageId,
            selectedCategoryId: input.selectedCategoryId,
            provider: input.provider,
            modelVersion: input.modelVersion,
            vectorId: input.vectorId,
            dimensions: input.dimensions,
            storageStatus: input.storageStatus || 'stored',
            createdAt: input.createdAt,
            expiresAt: input.expiresAt ?? null,
            deletedAt: null,
            failureReason: null
        };

        this.records.set(id, artifact);
        return cloneData(artifact);
    }

    async getVectorArtifact(id: string): Promise<VectorArtifact | null> {
        const record = this.records.get(id);
        return record ? cloneData(record) : null;
    }

    async listByUploadImageId(uploadImageId: string): Promise<VectorArtifact[]> {
        return Array.from(this.records.values())
            .filter((record) => record.uploadImageId === uploadImageId)
            .map((record) => cloneData(record));
    }

    async deleteByUploadImageId(input: {
        uploadImageId: string;
        requestedAt: string;
        reason: UploadImageDeleteApiRequest['reason'];
    }): Promise<UploadImageDeleteArtifactResult> {
        const records = Array.from(this.records.values())
            .filter((record) => record.uploadImageId === input.uploadImageId && record.storageStatus !== 'deleted');

        if (records.length === 0) {
            return artifactResult({
                status: 'no_records',
                operation: 'noop_delete',
                deletedAt: input.requestedAt,
                message: '삭제할 embedding vector artifact가 없습니다.'
            });
        }

        for (const record of records) {
            record.storageStatus = 'delete_requested';
            await this.provider?.deleteEmbedding({
                embeddingId: record.embeddingId,
                vectorId: record.vectorId,
                cropImageId: record.cropImageId,
                uploadImageId: record.uploadImageId,
                requestedAt: input.requestedAt,
                reason: input.reason
            });
            record.storageStatus = 'deleted';
            record.deletedAt = input.requestedAt;
            this.records.set(record.id, record);
        }

        return artifactResult({
            status: 'deleted',
            operation: 'delete',
            deletedAt: input.requestedAt,
            message: 'embedding vector artifact metadata deleted'
        });
    }
}

export async function createImageEmbeddingForSearchRequest(
    provider: EmbeddingProvider,
    request: ImageEmbeddingSearchRequest,
    options: CreateImageEmbeddingVectorArtifactOptions = {}
): Promise<ImageEmbeddingCreateResponse> {
    const cropImageId = assertCropImageId(request.cropImageId);
    return provider.createImageEmbedding(cropImageId, {
        sourceRequestId: request.sourceRequestId,
        uploadImageId: request.uploadImageId ?? null,
        searchTargetId: request.searchTargetId ?? null,
        selectedRegion: request.selectedRegion,
        selectedCrop: request.selectedCrop ?? null,
        selectedCategoryId: request.selectedCategoryId,
        subcategoryHint: request.subcategoryHint ?? null,
        brandHints: request.brandHints || [],
        ocrHints: request.ocrHints || [],
        regionSource: request.regionSource,
        userConfirmed: request.userConfirmed,
        selectedCandidateId: request.selectedCandidateId ?? null,
        privacyRedactionIds: request.privacyRedactionIds || [],
        externalAiPrivacyGate: request.externalAiPrivacyGate ?? null,
        cropUrl: request.cropUrl || null,
        expiresAt: options.expiresAt,
        retentionSeconds: options.retentionSeconds,
        now: options.now
    });
}

export async function createImageEmbeddingVectorArtifact(
    provider: EmbeddingProvider,
    repository: VectorArtifactRepository,
    request: ImageEmbeddingSearchRequest,
    options: CreateImageEmbeddingVectorArtifactOptions = {}
): Promise<{ embedding: ImageEmbeddingCreateResponse; artifact: VectorArtifact }> {
    const cropImageId = assertCropImageId(request.cropImageId);
    const embedding = await createImageEmbeddingForSearchRequest(provider, request, options);
    const artifact = await repository.createVectorArtifact({
        uploadImageId: request.uploadImageId ?? null,
        cropImageId,
        selectedCategoryId: request.selectedCategoryId,
        provider: provider.getProviderInfo().id,
        modelVersion: embedding.modelVersion,
        embeddingId: embedding.embeddingId,
        vectorId: embedding.vectorId,
        dimensions: embedding.dimensions,
        createdAt: embedding.createdAt,
        expiresAt: embedding.expiresAt,
        storageStatus: 'stored'
    });

    return { embedding, artifact };
}

export const mockEmbeddingProvider = new MockEmbeddingProvider();
export const inMemoryVectorArtifactRepository = new InMemoryVectorArtifactRepository(mockEmbeddingProvider);
